import { Alert, Button, Card, Descriptions, Form, Input, Space, Typography } from 'antd';
import { useMutation } from '@ngtrvu/use-api';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import linkAccountApiCall from 'apps/nhsv/apiCalls/nhsv/link-account';
import { CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export default function NHSVLinkAccountPage() {
  const [form] = Form.useForm();
  const [result, setResult] = useState<{ user_id: string; stock_account_id: string; account_number: string } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // @ts-ignore — local ApiCall shape differs from @ngtrvu/use-api's ApiCall type
  const { mutate, isPending } = useMutation(linkAccountApiCall, {
    onSuccess: (data: any) => {
      setResult(data);
      setErrorMsg(null);
      form.resetFields();
    },
    onError: (err: any) => {
      setErrorMsg(err?.message || 'Failed to link NHSV account');
      setResult(null);
    },
  });

  const onFinish = (values: any) => {
    setErrorMsg(null);
    setResult(null);
    mutate({
      account_number: values.account_number.trim(),
      name: (values.name || '').trim(),
      email: values.email.trim(),
      phone: (values.phone || '').trim(),
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <Title level={3}>Link NHSV Account to Stag</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Enter the NHSV trading account number and user details to create a linked Stag account.
        The trading account must exist in NHSV before linking.
      </Text>

      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="NHSV Trading Account Number"
            name="account_number"
            rules={[{ required: true, message: 'Trading account number is required' }]}
            extra="The NHSV account number (e.g. 20C123456)"
          >
            <Input placeholder="Enter trading account number" allowClear />
          </Form.Item>

          <Form.Item
            label="User Email"
            name="email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item label="Full Name" name="name">
            <Input placeholder="Nguyễn Văn A" />
          </Form.Item>

          <Form.Item label="Phone Number" name="phone">
            <Input placeholder="0912345678" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Link Account
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {errorMsg && (
        <Alert
          type="error"
          message="Link Failed"
          description={errorMsg}
          showIcon
          style={{ marginTop: 24 }}
        />
      )}

      {result && (
        <Card
          style={{ marginTop: 24 }}
          title={
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              Account Linked Successfully
            </Space>
          }
        >
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="User ID">{result.user_id}</Descriptions.Item>
            <Descriptions.Item label="Stock Account ID">{result.stock_account_id}</Descriptions.Item>
            <Descriptions.Item label="Trading Account Number">
              <Link to={`/nhsv/account-detail?account_number=${result.account_number}`}>
                {result.account_number}
              </Link>
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16 }}>
            <Space>
              <Link to={`/nhsv/account-detail?account_number=${result.account_number}`}>
                <Button type="primary">View Account Detail</Button>
              </Link>
            </Space>
          </div>
        </Card>
      )}
    </div>
  );
}
